// A timber plantation, described the way age-of-mcp authors it: `tree`, `grove`,
// `furrows`, `soil` and `logpile` helpers plus the shared `fenceRect`, walked
// through its build stages. Ported 1:1 from age-of-mcp's tree_farm.ts. The model
// library has no terrain, so the warped ground grid is flattened: the soil is a
// flat slab and every surface sample (`surf`) is 0 (y = 0 is the floor).
import * as THREE from "three";
import { defineModel, fenceRect, pace, type Part } from "../../lib";

const SOIL  = "#5c4327";
const RIDGE = "#4d3820";
const TRUNK = "#6e4a2a";
const LEAF  = "#3f7d3a";
const LEAFD = "#2f6330";
const LOG   = "#7a5630";

// The plantation is FOOTPRINT-NATIVE (4x4): saplings planted in neat rows that
// grow into mature trees across the build stages.
const HALF = 1.92;                                   // reaches +/-1.92 tiles (just inside 4x4)
const SPOTS = [-1.4, -0.47, 0.47, 1.4] as const;     // 4x4 grid of planting spots

// Flat ground: the terrain surface is 0 everywhere.
function surf(_x: number, _z: number): number {
  return 0;
}

// Tilled soil — a flat box slab over the whole footprint (the flat equivalent of
// the terrain-warped grid slab: top at y = 0, bottom `thickness` below).
function soil(): Part {
  const thickness = 0.12;
  const h = HALF, top = 0, yb = -thickness;
  const pos: number[] = [];
  const quad = (p0: number[], p1: number[], p2: number[], p3: number[]): void => {
    pos.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3);
  };
  const tSW = [-h, top, -h], tSE = [h, top, -h];
  const tNW = [-h, top,  h], tNE = [h, top,  h];
  const bSW = [-h, yb, -h], bSE = [h, yb, -h];
  const bNW = [-h, yb,  h], bNE = [h, yb,  h];
  quad(tSW, tNW, tNE, tSE);   // top    (+Y)
  quad(bSW, bSE, bNE, bNW);   // bottom (-Y)
  quad(tNW, bNW, bNE, tNE);   // north  (+Z)
  quad(tSE, bSE, bSW, tSW);   // south  (-Z)
  quad(tSE, tNE, bNE, bSE);   // east   (+X)
  quad(tSW, bSW, bNW, tNW);   // west   (-X)
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color: SOIL, position: [0, 0, 0] };
}

// Plough furrows running across the plot.
function furrows(): Part[] {
  return [-1.4, -0.7, 0, 0.7, 1.4].map((z): Part => ({
    geometry: new THREE.BoxGeometry(HALF * 1.95, 0.04, 0.09),
    color: RIDGE,
    position: [0, surf(0, z) + 0.06, z],
  }));
}

// One low-poly tree at scale `k` (0 = sapling ... 1 = mature) on the soil.
function tree(x: number, z: number, k: number): Part[] {
  const y0 = surf(x, z) + 0.04;
  const th = 0.5 * k;                  // trunk height
  const fr = 0.34 * k;                 // foliage radius
  const parts: Part[] = [
    { geometry: new THREE.CylinderGeometry(0.05 * k + 0.02, 0.07 * k + 0.02, th + 0.05, 5), color: TRUNK, position: [x, y0 + (th + 0.05) / 2, z] },
  ];
  // Two stacked cones for a conical crown.
  parts.push({ geometry: new THREE.ConeGeometry(fr, fr * 1.6, 6), color: LEAF, position: [x, y0 + th + fr * 0.7, z] });
  if (k > 0.5) parts.push({ geometry: new THREE.ConeGeometry(fr * 0.7, fr * 1.3, 6), color: LEAFD, position: [x, y0 + th + fr * 1.4, z] });
  return parts;
}

function grove(k: number): Part[] {
  const out: Part[] = [];
  for (const x of SPOTS) for (const z of SPOTS) out.push(...tree(x, z, k));
  return out;
}

// A small stack of cut logs in a corner — the plantation's harvested timber.
function logpile(): Part[] {
  const y0 = surf(1.55, 1.55) + 0.04;
  return [0, 1].map((i): Part => ({
    geometry: new THREE.CylinderGeometry(0.07, 0.07, 0.5, 6),
    color: LOG,
    position: [1.55, y0 + 0.07 + i * 0.14, 1.55],
    rotation: [0, 0, Math.PI / 2],
  }));
}

// The build stages of the tree farm: bare tilled rows, planted saplings, young
// trees, and a grove of mature trees with a perimeter rail fence and a log pile.
function partsFor(s: number): Part[] {
  if (s <= 0) return [soil(), ...furrows()];
  if (s === 1) return [soil(), ...furrows(), ...grove(0.35)];
  if (s === 2) return [soil(), ...furrows(), ...grove(0.65)];
  const yc = surf(0, 0);
  return [
    soil(), ...furrows(), ...grove(1),
    ...fenceRect(-1.86, -1.86, 1.86, 1.86, yc + 0.05),
    ...logpile(),
  ];
}

export const treeFarm = defineModel({
  id: "tree-farm",
  tiles: { w: 4, h: 4 },
  batchId: "standard",
  name: "Tree farm",
  tags: ["building","tree","farm","low-poly","age-of-mcp","classic"],
  summary: "Low-poly tree farm.",
  description: "A timber plantation of planted trees grown for wood.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "grove", position: [0, 0, 0], facing: 0, capacity: 6, description: "The sapling rows a forester paces." },
  ],
  interactions: [
    { id: "tend", label: "Tending the saplings", station: "grove", variants: ["complete"], ...pace([[-1.55, -1.55], [1.55, -1.55], [1.55, 0], [-1.55, 0], [-1.55, 1.55], [1.55, 1.55]]) },
  ],
});
